import axiosInstance from '../../../config/axios';
import { CrmTask } from '../../sales/services/CrmTaskService';
import ShiftService, { Shift, DayShifts, ShiftCreatePayload } from '../../shifts/services/ShiftService';
import {
  formatLocalDate,
  formatLocalDateTime,
  TIME_CONSTANTS,
} from '../utils/plantafelTime';
import { pdebug, pdebugWarn, pdebugError } from '../utils/plantafelDebug';

const { MINUTES_PER_DAY, MINUTES_PER_WEEK } = TIME_CONSTANTS;

/**
 * Unified helper to parse list responses from various API formats.
 * Checks for direct array, then tries each key in order.
 */
function parseList<T>(data: any, keys: string[]): T[] {
  if (Array.isArray(data)) return data;
  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
  }
  return [];
}

export interface WorkArea {
  id: number;
  name: string;
  color?: string;
  location?: number;
  location_name?: string;
}

export interface TeamMember {
  id: number;  // roster_member_id for DELETE
  employee_id: number;
  employee_name?: string;  // API returns full name as single string
  first_name?: string;
  last_name?: string;
  role?: string;
  is_lead?: boolean;
}

// FE-OPS-R22-3: TeamAsset interface for team roster assets
export interface TeamAsset {
  id: number;  // team_asset_id for DELETE
  asset_id: number;
  asset_name?: string;
  asset_code?: string;
  license_plate?: string;
  asset_type?: string;
}

export interface LaneTeam {
  id: number;
  name: string;
  work_area: number;
  work_area_name: string;
  work_area_color?: string;
  roster_members?: TeamMember[];
  roster_assets?: TeamAsset[];
  is_active?: boolean;
}

export interface LaneGroup {
  workAreaId: number;
  workAreaName: string;
  workAreaColor?: string;
  teams: LaneTeam[];
}

export interface OperationsSettings {
  day_start: string;
  day_end: string;
  day_start_minutes: number;
  day_end_minutes: number;
  snap_minutes: number;
  default_shift_duration_hours: number;
}

export interface ScheduleBacklogPayload {
  work_area_team_id: number;
  start_time?: string;
  end_time?: string;
  full_day?: boolean;
}

export interface PlantafelData {
  laneGroups: LaneGroup[];
  shifts: Shift[];
  backlogTasks: CrmTask[];
  backlogMeta: BacklogMeta | null;
  backlogPagination: BacklogPagination | null;
  weekStart: string;
  settings: OperationsSettings;
}

export interface ProjectTimelineItem {
  id: number;
  name: string;
  partner_id?: number;
  partner_name?: string;
  status: string;
  // Planned dates (from project settings)
  planned_start?: string | null;
  planned_end?: string | null;
  // Timeline dates (for display - either planned or derived from shifts)
  timeline_start?: string;
  timeline_end?: string;
  timeline_source?: 'planned' | 'shifts';
  // Metadata
  jobs_count_in_range?: number;
  milestones_ready_count?: number;
  city?: string;
  has_payment_plan?: boolean;
}

// FE-OPS-R24-3: Project Dossier interfaces
export interface ProjectDossier {
  id: number;
  name: string;
  partner_id?: number;
  partner_name?: string;
  status: string;
  planned_start?: string | null;
  planned_end?: string | null;
  city?: string;
  street?: string;
  postal_code?: string;
  description?: string;
  notes?: string;
  // KPIs
  total_jobs?: number;
  completed_jobs?: number;
  upcoming_jobs?: number;
  total_hours_planned?: number;
  total_hours_worked?: number;
  // Sites
  sites_count?: number;
}

// FE-OPS-R24-1: Site interfaces
export interface ProjectSite {
  id: number;
  project: number;
  name: string;
  address?: string;
  access_info?: string;
  parking_info?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateSitePayload {
  name: string;
  address?: string;
  access_info?: string;
  parking_info?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  notes?: string;
}

export interface UpdateSitePayload extends Partial<CreateSitePayload> {}

// FE-OPS-R24-3: Job shift for project
export interface ProjectJobShift {
  id: number;
  crm_task_id?: number;
  title: string;
  status: string;
  start_time: string;
  end_time: string;
  work_area_team_id?: number;
  work_area_team_name?: string;
  site_id?: number;
  site_name?: string;
  assigned_employees_count?: number;
}

// FE-OPS-R25-1: Milestone interfaces
export interface MilestoneTask {
  id: number;
  title: string;
  status: string;
  is_done?: boolean;
}

// FE-OPS-R26-2: Jobs linked to a milestone
export interface MilestoneJob {
  id: number;
  title: string;
  status: string;
  checklist_total?: number;
  checklist_done?: number;
  start_time?: string;
  shift_id?: number;
}

// FE-OPS-R27-2: Positions (Leistungen) linked to a milestone
export interface MilestonePosition {
  id: number;
  name: string;
  amount: number;
}

export interface ProjectMilestone {
  id: number;
  label: string;
  percent: number;
  status: 'OPEN' | 'IN_PROGRESS' | 'READY_FOR_INVOICE' | 'INVOICED' | 'PAID';
  computed?: {
    percent_done: number;
    tasks_total: number;
    tasks_done: number;
  };
  tasks?: MilestoneTask[];
  jobs?: MilestoneJob[];
  positions?: MilestonePosition[];
  positions_total_net?: number;
  invoice_id?: number;
  invoice_number?: string;
  amount?: number;
  due_date?: string;
}

// FE-OPS-R27-3: Full milestones response with offer metadata
export interface ProjectMilestonesResponse {
  project_id: number;
  offer_id: number | null;
  payment_plan_id: number | null;
  has_payment_plan: boolean;
  milestones: ProjectMilestone[];
  meta?: {
    offers_with_payment_plan?: number;
    selection_strategy?: string;
  };
}

// FE-OPS-R28-3: Generate Jobs from Milestone Positions
export interface GenerateJobsPayload {
  offer_id?: number;
  mode?: 'APPEND' | 'REPLACE';
}

export interface GeneratedTask {
  id: number;
  title: string;
  payment_milestone_id: number;
  source_offer_line_id: number;
  checklist_total: number;
}

export interface GenerateJobsResponse {
  project_id: number;
  offer_id: number;
  payment_milestone_id: number;
  created_tasks_count: number;
  existing_tasks_count: number;
  tasks: GeneratedTask[];
}

export interface BacklogMeta {
  excluded_because_scheduled: number;
  excluded_because_no_project: number;
  excluded_because_done: number;
  total_tasks_checked?: number;
}

export interface BacklogPagination {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export interface BacklogResponse {
  tasks: CrmTask[];
  meta: BacklogMeta | null;
  pagination: BacklogPagination | null;
}

export interface PlantafelShift extends Shift {
  task?: CrmTask | null;
}

export interface GanttShiftPosition {
  shift: Shift;
  task?: CrmTask | null;
  startDayIndex: number;
  endDayIndex: number;
  lane: number;
  left: number;
  width: number;
  // Clamping info for shifts extending beyond week
  clampedLeft: boolean;
  clampedRight: boolean;
  // Original unclamped values for drag calculations
  originalStartMinutes: number;
  originalEndMinutes: number;
}

// FE-OPS-R25-2: Operations Template interfaces
export interface OperationsTemplateMilestone {
  id?: number;
  code?: string;
  label: string;
  percent: number;
  order: number;
}

export interface OperationsTemplateJob {
  title: string;
  description?: string;
  estimated_hours?: number;
  milestone_label?: string;
  order: number;
}

export interface OperationsTemplate {
  id: number;
  code: string;
  name: string;
  description?: string;
  milestones: OperationsTemplateMilestone[];
  jobs: OperationsTemplateJob[];
  milestones_count?: number;
  jobs_count?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

// FE-OPS-R26-1: Milestone mapping for template application
export interface MilestoneMapping {
  template_milestone_id: number;
  payment_milestone_id: number;
}

export interface ApplyTemplatePayload {
  template_id: number;
  start_date?: string;
  milestone_mapping?: MilestoneMapping[];
}

export interface ApplyTemplateResult {
  jobs_created: number;
  milestones_created: number;
  message?: string;
}

// FE-OPS-R25-3: Job Checklist interfaces
export interface ChecklistItem {
  id: number;
  job_id: number;
  title?: string;
  name?: string;
  text?: string;  // From OfferLineScopeItem (catalog subtask)
  is_done: boolean;
  order: number;
  done_by?: number;
  done_by_name?: string;
  done_at?: string;
  created_at?: string;
  // Subtask comments & attachments counts
  comments_count?: number;
  attachments_count?: number;
}

export interface CreateChecklistItemPayload {
  text: string;
  order?: number;
}

export interface UpdateChecklistItemPayload {
  title?: string;
  is_done?: boolean;
  order?: number;
}

// FE-OPS-R25-3: Job Detail with milestone info
export interface JobDetail {
  id: number;
  title: string;
  description?: string;
  status: string;
  priority: string;
  project_id?: number;
  project_name?: string;
  partner_id?: number;
  partner_name?: string;
  offer_id?: number;
  offer_number?: string;
  offer_title?: string;
  payment_milestone_id?: number;
  payment_milestone_label?: string;
  ops_milestone_id?: number;
  ops_milestone_label?: string;
  checklist_progress?: {
    total: number;
    done: number;
    percent: number;
  };
}

const BASE = '/api/admin/operations/';
const TEAMS_ENDPOINT = '/api/admin/work-area-teams/';

const DEFAULT_SETTINGS: OperationsSettings = {
  day_start: '06:00',
  day_end: '18:00',
  day_start_minutes: 6 * 60,
  day_end_minutes: 18 * 60,
  snap_minutes: 30,
  default_shift_duration_hours: 8,
};

export default class PlantafelService {
  static async fetchOperationsSettings(): Promise<OperationsSettings> {
    try {
      const response = await axiosInstance.get<any>(`${BASE}settings/`);
      const data = response.data;
      const dayStart = data.day_start || '06:00';
      const dayEnd = data.day_end || '18:00';
      const [startH, startM] = dayStart.split(':').map(Number);
      const [endH, endM] = dayEnd.split(':').map(Number);
      return {
        day_start: dayStart,
        day_end: dayEnd,
        day_start_minutes: startH * 60 + startM,
        day_end_minutes: endH * 60 + endM,
        snap_minutes: data.snap_minutes || data.grid?.snap_minutes || 30,
        default_shift_duration_hours: data.default_shift_duration_hours || 8,
      };
    } catch (err) {
      pdebugWarn('Failed to load operations settings, using defaults:', err);
      return DEFAULT_SETTINGS;
    }
  }

  /**
   * Fetches lane teams from the operations API AND all work areas.
   * WorkAreas without teams are included with empty teams array (displayed like Shiftplan).
   * WorkAreas with teams are displayed with team rows below a header.
   *
   * @param weekStart - Optional week start date (YYYY-MM-DD) for weekly roster filtering
   */
  static async fetchLaneTeams(weekStart?: string): Promise<LaneGroup[]> {
    const weekParam = weekStart ? `&week=${weekStart}` : '';
    const url = `${BASE}lane-teams/?include_defaults=1${weekParam}`;
    pdebug('fetchLaneTeams: calling', url);

    // Fetch teams and all work areas in parallel
    const [teamsResponse, workAreasResponse] = await Promise.all([
      axiosInstance.get<any>(url),
      axiosInstance.get<any>('/api/admin/work-areas/').catch((err) => {
        pdebugWarn('Failed to fetch work-areas, continuing with teams only:', err);
        return { data: [] };
      }),
    ]);

    const teamsData = teamsResponse.data;
    const workAreasData = workAreasResponse.data;

    pdebug('fetchLaneTeams: raw teams response', teamsData);
    pdebug('fetchLaneTeams: raw workAreas response', workAreasData);

    // Parse teams
    let teams: any[] = [];
    if (teamsData) {
      if (Array.isArray(teamsData)) {
        teams = teamsData;
      } else if (teamsData.teams && Array.isArray(teamsData.teams)) {
        teams = teamsData.teams;
      } else if (teamsData.results && Array.isArray(teamsData.results)) {
        teams = teamsData.results;
      }
    }

    // Parse work areas
    let workAreas: any[] = [];
    if (workAreasData) {
      if (Array.isArray(workAreasData)) {
        workAreas = workAreasData;
      } else if (workAreasData.results && Array.isArray(workAreasData.results)) {
        workAreas = workAreasData.results;
      }
    }

    pdebug('fetchLaneTeams: parsed teams count:', teams.length, 'workAreas count:', workAreas.length);

    // Group teams by work area
    const groupMap = new Map<number, LaneGroup>();

    // First, add all work areas (including those without teams)
    workAreas.forEach((wa: any) => {
      const waId = wa.id;
      if (waId === undefined || waId === null) return;

      if (!groupMap.has(waId)) {
        groupMap.set(waId, {
          workAreaId: waId,
          workAreaName: wa.name || `WorkArea ${waId}`,
          workAreaColor: wa.color,
          teams: [],
        });
      }
    });

    // Then, add teams to their work areas
    teams.forEach((team: any, idx: number) => {
      const workAreaId = team.work_area_id ?? team.work_area;

      if (workAreaId === undefined || workAreaId === null) {
        pdebugWarn(`Team at index ${idx} has no work_area_id or work_area:`, team);
        return;
      }

      if (!groupMap.has(workAreaId)) {
        groupMap.set(workAreaId, {
          workAreaId: workAreaId,
          workAreaName: team.work_area_name || `WorkArea ${workAreaId}`,
          workAreaColor: team.work_area_color || team.color,
          teams: [],
        });
      }

      // Skip inactive teams
      const isActive = team.is_active !== false;
      if (!isActive) {
        pdebug(`Skipping inactive team: ${team.name} (id=${team.id})`);
        return;
      }

      const normalizedTeam: LaneTeam = {
        id: team.id,
        name: team.name,
        work_area: workAreaId,
        work_area_name: team.work_area_name || groupMap.get(workAreaId)!.workAreaName,
        work_area_color: team.work_area_color || team.color,
        roster_members: team.roster_members || team.members || [],
        roster_assets: team.roster_assets || team.assets || [],
        is_active: isActive,
      };

      groupMap.get(workAreaId)!.teams.push(normalizedTeam);
    });

    const result = Array.from(groupMap.values());

    // Sort: WAs without teams first, then WAs with teams
    result.sort((a, b) => {
      const aHasTeams = a.teams.length > 0;
      const bHasTeams = b.teams.length > 0;
      if (aHasTeams === bHasTeams) {
        return a.workAreaName.localeCompare(b.workAreaName);
      }
      return aHasTeams ? 1 : -1;
    });

    pdebug('fetchLaneTeams: returning', result.length, 'groups (with',
      result.filter(g => g.teams.length === 0).length, 'without teams)');

    return result;
  }

  /**
   * Fetches shifts from the dedicated Plantafel endpoint.
   * This endpoint is optimized for job shifts and supports include_regular_shifts parameter.
   */
  static async fetchPlantafelShifts(
    weekStart: string,
    includeRegularShifts: boolean = false,
    days: number = 7
  ): Promise<Shift[]> {
    // Backend has a limit on days per request - chunk large requests
    const MAX_DAYS_PER_REQUEST = 31;

    pdebug('fetchPlantafelShifts: days requested:', days);

    // If within limit, make single request
    if (days <= MAX_DAYS_PER_REQUEST) {
      return this.fetchPlantafelShiftsChunk(weekStart, includeRegularShifts, days);
    }

    // Chunk large requests into parallel fetches
    pdebug('fetchPlantafelShifts: Chunking', days, 'days into', Math.ceil(days / MAX_DAYS_PER_REQUEST), 'requests');

    const chunks: { start: string; days: number }[] = [];
    let currentDate = new Date(weekStart);
    let remainingDays = days;

    while (remainingDays > 0) {
      const chunkDays = Math.min(remainingDays, MAX_DAYS_PER_REQUEST);
      chunks.push({
        start: currentDate.toISOString().split('T')[0],
        days: chunkDays,
      });
      currentDate.setDate(currentDate.getDate() + chunkDays);
      remainingDays -= chunkDays;
    }

    // Fetch all chunks in parallel
    const chunkResults = await Promise.all(
      chunks.map(chunk => this.fetchPlantafelShiftsChunk(chunk.start, includeRegularShifts, chunk.days))
    );

    // Merge and deduplicate
    const shiftMap = new Map<number, Shift>();
    chunkResults.forEach(shifts => {
      shifts.forEach(shift => {
        if (!shiftMap.has(shift.id)) {
          shiftMap.set(shift.id, shift);
        }
      });
    });

    const allShifts = Array.from(shiftMap.values());
    pdebug('fetchPlantafelShifts: Total unique shifts from chunks:', allShifts.length);

    return allShifts;
  }

  /**
   * Fetch a single chunk of shifts (internal helper)
   */
  private static async fetchPlantafelShiftsChunk(
    weekStart: string,
    includeRegularShifts: boolean,
    days: number
  ): Promise<Shift[]> {
    try {
      const shiftsData = await ShiftService.fetchWeeklyShifts({
        start: weekStart,
        days: days,
        include_regular_shifts: includeRegularShifts ? 1 : 0,
        include_assets: 1,
        include_employees: 1,
      });

      // Deduplicate shifts by ID (weekly-shifts returns same shift for each day it spans)
      const shiftMap = new Map<number, Shift>();
      shiftsData.forEach((day: DayShifts) => {
        (day.shifts || []).forEach((shift: Shift) => {
          if (!shiftMap.has(shift.id)) {
            shiftMap.set(shift.id, shift);
          }
        });
      });

      return Array.from(shiftMap.values());
    } catch (err: any) {
      pdebugError('fetchPlantafelShiftsChunk failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Fetch backlog tasks from the operations endpoint.
   * This endpoint only returns tasks with project_id that don't have shifts in the date range.
   *
   * @param fromDate - Start date (YYYY-MM-DD)
   * @param toDate - End date (YYYY-MM-DD)
   * @param includeMeta - If true, includes debug metadata about excluded tasks
   * @param limit - Maximum number of tasks to return (default: 50, max: 200)
   * @param offset - Number of tasks to skip for pagination (default: 0)
   */
  static async fetchBacklogTasks(
    fromDate: string,
    toDate: string,
    includeMeta: boolean = false,
    limit: number = 50,
    offset: number = 0
  ): Promise<BacklogResponse> {
    const params = new URLSearchParams({
      from: fromDate,
      to: toDate,
      limit: String(limit),
      offset: String(offset),
    });
    if (includeMeta) {
      params.append('include_meta', '1');
    }
    const url = `${BASE}backlog-tasks/?${params.toString()}`;
    pdebug('fetchBacklogTasks URL:', url);

    try {
      const response = await axiosInstance.get<any>(url);
      pdebug('fetchBacklogTasks raw response:', response.data);

      // Handle different response formats
      let tasks: CrmTask[];
      let meta: BacklogMeta | null = null;
      let pagination: BacklogPagination | null = null;

      if (Array.isArray(response.data)) {
        tasks = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        tasks = response.data.results;
        meta = response.data.meta || null;
        pagination = response.data.pagination || null;
      } else if (response.data?.tasks && Array.isArray(response.data.tasks)) {
        tasks = response.data.tasks;
        meta = response.data.meta || null;
        pagination = response.data.pagination || null;
      } else {
        pdebugWarn('Unexpected backlog response format:', response.data);
        tasks = [];
      }

      pdebug('fetchBacklogTasks result:', tasks.length, 'tasks', 'meta:', meta, 'pagination:', pagination);
      return { tasks, meta, pagination };
    } catch (err: any) {
      pdebugError('fetchBacklogTasks failed:', err?.response?.status, err?.message);
      return { tasks: [], meta: null, pagination: null };
    }
  }

  /**
   * Fetches projects timeline data for displaying project bars above the Gantt chart.
   * Returns projects that have planned_start or planned_end within the date range.
   */
  static async fetchProjectsTimeline(fromDate: string, toDate: string): Promise<ProjectTimelineItem[]> {
    const url = `${BASE}projects-timeline/?from=${fromDate}&to=${toDate}`;
    pdebug('fetchProjectsTimeline URL:', url);

    try {
      const response = await axiosInstance.get<any>(url);
      pdebug('fetchProjectsTimeline raw response:', response.data);

      let projects: ProjectTimelineItem[];
      if (Array.isArray(response.data)) {
        projects = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        projects = response.data.results;
      } else if (response.data?.projects && Array.isArray(response.data.projects)) {
        projects = response.data.projects;
      } else {
        pdebugWarn('Unexpected projects-timeline response format:', response.data);
        projects = [];
      }

      pdebug('fetchProjectsTimeline result:', projects.length, 'projects');
      return projects;
    } catch (err: any) {
      pdebugError('fetchProjectsTimeline failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Fetches ALL active projects for the Projekte tab in LeftPanel.
   * Projects can be scheduled multiple times, so we show all of them.
   */
  static async fetchAllProjects(search?: string): Promise<ProjectTimelineItem[]> {
    // Fetch all projects without status filter so all are visible in Plantafel
    let url = `${BASE}projects/`;
    if (search) {
      url += `?q=${encodeURIComponent(search)}`;
    }
    pdebug('fetchAllProjects URL:', url);

    try {
      const response = await axiosInstance.get<any>(url);
      pdebug('fetchAllProjects raw response:', response.data);

      let rawProjects: any[];
      if (Array.isArray(response.data)) {
        rawProjects = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        rawProjects = response.data.results;
      } else if (response.data?.projects && Array.isArray(response.data.projects)) {
        rawProjects = response.data.projects;
      } else {
        pdebugWarn('Unexpected projects response format:', response.data);
        rawProjects = [];
      }

      // Map planned_start/planned_end to timeline_start/timeline_end for timeline rendering
      const projects: ProjectTimelineItem[] = rawProjects.map(p => ({
        ...p,
        timeline_start: p.timeline_start || p.planned_start || null,
        timeline_end: p.timeline_end || p.planned_end || null,
        timeline_source: p.timeline_source || (p.planned_start ? 'planned' : undefined),
      }));

      pdebug('fetchAllProjects result:', projects.length, 'projects');
      return projects;
    } catch (err: any) {
      pdebugError('fetchAllProjects failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * @deprecated Use fetchAllProjects instead
   */
  static async fetchUnplannedProjects(search?: string): Promise<ProjectTimelineItem[]> {
    return this.fetchAllProjects(search);
  }

  static async fetchPlantafelData(
    weekStart: string,
    includeRegularShifts: boolean = false,
    includeBacklogMeta: boolean = true,
    days: number = 7
  ): Promise<PlantafelData> {
    pdebug('fetchPlantafelData:', { weekStart, includeRegularShifts, includeBacklogMeta, days });

    // Calculate end date based on days parameter
    const startDate = new Date(weekStart);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days - 1);
    const weekEnd = endDate.toISOString().split('T')[0];

    const [laneGroups, shifts, backlogResponse, settings] = await Promise.all([
      this.fetchLaneTeams(weekStart),
      this.fetchPlantafelShifts(weekStart, includeRegularShifts, days),
      this.fetchBacklogTasks(weekStart, weekEnd, includeBacklogMeta),
      this.fetchOperationsSettings(),
    ]);

    pdebug('Data loaded:', {
      laneGroups: laneGroups.length,
      shifts: shifts.length,
      backlogTasks: backlogResponse.tasks.length,
      backlogMeta: backlogResponse.meta,
      backlogPagination: backlogResponse.pagination,
    });

    return {
      laneGroups,
      shifts,
      backlogTasks: backlogResponse.tasks,
      backlogMeta: backlogResponse.meta,
      backlogPagination: backlogResponse.pagination,
      weekStart,
      settings,
    };
  }

  /**
   * Schedules a backlog task using FULL_DAY mode (V1 default).
   * This eliminates "released too early" issues by always creating full-day shifts.
   *
   * @param taskId - The backlog task ID
   * @param teamId - The work_area_team ID
   * @param startDate - The date string (YYYY-MM-DD)
   * @param mode - Schedule mode: 'FULL_DAY' (default) or 'CUSTOM'
   * @param startTime - Optional start time for CUSTOM mode
   * @param endTime - Optional end time for CUSTOM mode
   */
  static async scheduleBacklogTask(
    taskId: number,
    teamId: number,
    startDate: string,
    mode: 'FULL_DAY' | 'CUSTOM' = 'FULL_DAY',
    startTime?: string,
    endTime?: string
  ): Promise<Shift> {
    pdebug('scheduleBacklogTask called:', {
      taskId,
      teamId,
      startDate,
      mode,
      startTime,
      endTime,
    });

    const payload = mode === 'FULL_DAY'
      ? {
          work_area_team_id: teamId,
          start_date: startDate,
          mode: 'FULL_DAY',
        }
      : {
          work_area_team_id: teamId,
          start_time: startTime,
          end_time: endTime,
          mode: 'CUSTOM',
        };

    pdebug('POST payload:', payload);

    try {
      const response = await axiosInstance.post<{ shift: Shift } | Shift>(
        `${BASE}backlog-tasks/${taskId}/schedule/`,
        payload
      );
      // Backend returns { shift: {...} } - extract the shift object
      const shift = (response.data as any).shift || response.data;

      pdebug('scheduleBacklogTask success:', {
        shiftId: shift.id,
        'payload.work_area_team_id': payload.work_area_team_id,
        'response.work_area_team_id': shift.work_area_team_id,
        match: payload.work_area_team_id === shift.work_area_team_id,
      });

      if (payload.work_area_team_id !== shift.work_area_team_id) {
        pdebugError('TEAM ID MISMATCH! FE sent:', payload.work_area_team_id, 'BE returned:', shift.work_area_team_id);
      } else {
        pdebug('Team ID verified:', payload.work_area_team_id);
      }

      return shift;
    } catch (err: any) {
      pdebugError('scheduleBacklogTask failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      throw err;
    }
  }

  /**
   * Legacy method for backward compatibility - uses CUSTOM mode internally.
   */
  static async scheduleBacklogTaskCustom(
    taskId: number,
    teamId: number,
    startTime: string,
    endTime: string
  ): Promise<Shift> {
    return this.scheduleBacklogTask(taskId, teamId, startTime.split('T')[0], 'CUSTOM', startTime, endTime);
  }

  static async fetchTeamRoster(teamId: number): Promise<TeamMember[]> {
    try {
      const response = await axiosInstance.get<TeamMember[]>(
        `${BASE}work-area-teams/${teamId}/members/`
      );
      return response.data;
    } catch (err) {
      pdebugWarn('Failed to fetch team roster:', err);
      return [];
    }
  }

  static async updateTeamRoster(teamId: number, employeeIds: number[]): Promise<TeamMember[]> {
    const response = await axiosInstance.patch<TeamMember[]>(
      `${BASE}work-area-teams/${teamId}/members/`,
      { employee_ids: employeeIds }
    );
    return response.data;
  }

  /**
   * Adds a single member to a team roster.
   * POST /api/admin/operations/work-area-teams/{teamId}/members/
   */
  static async addTeamMember(teamId: number, employeeId: number, role: string = 'MEMBER'): Promise<TeamMember> {
    const url = `${BASE}work-area-teams/${teamId}/members/`;
    pdebug('addTeamMember:', { teamId, employeeId, role });

    try {
      const response = await axiosInstance.post<TeamMember>(url, {
        employee_id: employeeId,
        role: role,
      });
      pdebug('addTeamMember success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('addTeamMember failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Removes a member from a team roster.
   * DELETE /api/admin/operations/work-area-team-members/{memberId}/
   */
  static async removeTeamMember(memberId: number): Promise<void> {
    const url = `${BASE}work-area-team-members/${memberId}/`;
    pdebug('removeTeamMember:', { memberId });

    try {
      await axiosInstance.delete(url);
      pdebug('removeTeamMember success');
    } catch (err: any) {
      pdebugError('removeTeamMember failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Adds an asset to a team.
   * POST /api/admin/operations/work-area-teams/{teamId}/assets/
   */
  static async addTeamAsset(teamId: number, assetId: number): Promise<{ id: number; asset_id: number; asset_name?: string }> {
    const url = `${BASE}work-area-teams/${teamId}/assets/`;
    pdebug('addTeamAsset:', { teamId, assetId });

    try {
      const response = await axiosInstance.post<{ id: number; asset_id: number; asset_name?: string }>(url, {
        asset_id: assetId,
      });
      pdebug('addTeamAsset success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('addTeamAsset failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Removes an asset from a team.
   * DELETE /api/admin/operations/work-area-team-assets/{id}/
   */
  static async removeTeamAsset(teamAssetId: number): Promise<void> {
    const url = `${BASE}work-area-team-assets/${teamAssetId}/`;
    pdebug('removeTeamAsset:', { teamAssetId });

    try {
      await axiosInstance.delete(url);
      pdebug('removeTeamAsset success');
    } catch (err: any) {
      pdebugError('removeTeamAsset failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  // ============================================================================
  // FE-OPS-R25: Weekly Roster API Methods
  // ============================================================================

  /**
   * Weekly roster response structure
   */
  static weeklyRosterInterface = {} as {
    effective_from_week: string;
    is_override: boolean;
    members: TeamMember[];
    assets: TeamAsset[];
  };

  /**
   * Get effective roster for a specific week.
   * GET /api/admin/operations/work-area-teams/{id}/roster/?week=YYYY-MM-DD
   */
  static async getWeeklyRoster(teamId: number, week: string): Promise<{
    effective_from_week: string;
    is_override: boolean;
    members: TeamMember[];
    assets: TeamAsset[];
  }> {
    const url = `${BASE}work-area-teams/${teamId}/roster/?week=${week}`;
    pdebug('getWeeklyRoster:', { teamId, week });

    try {
      const response = await axiosInstance.get<any>(url);
      pdebug('getWeeklyRoster success:', response.data);
      return {
        effective_from_week: response.data.effective_from_week || week,
        is_override: response.data.is_override || false,
        members: response.data.members || [],
        assets: response.data.assets || [],
      };
    } catch (err: any) {
      pdebugError('getWeeklyRoster failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Set/update roster for a specific week.
   * PUT /api/admin/operations/work-area-teams/{id}/roster/?week=YYYY-MM-DD
   *
   * Backend expects format:
   * { members: [{ employee_id, role }], assets: [{ asset_id }] }
   */
  static async setWeeklyRoster(
    teamId: number,
    week: string,
    memberIds: number[],
    assetIds: number[]
  ): Promise<void> {
    const url = `${BASE}work-area-teams/${teamId}/roster/?week=${week}`;

    // Convert to backend format
    const members = memberIds.map((id) => ({ employee_id: id, role: 'MEMBER' }));
    const assets = assetIds.map((id) => ({ asset_id: id }));

    pdebug('setWeeklyRoster:', { teamId, week, members, assets });

    try {
      await axiosInstance.put(url, { members, assets });
      pdebug('setWeeklyRoster success');
    } catch (err: any) {
      pdebugError('setWeeklyRoster failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * List all weekly roster overrides for a team.
   * GET /api/admin/operations/work-area-teams/{id}/roster-weeks/
   */
  static async getWeeklyRosterOverrides(teamId: number): Promise<{
    week: string;
    member_count: number;
    asset_count: number;
  }[]> {
    const url = `${BASE}work-area-teams/${teamId}/roster-weeks/`;
    pdebug('getWeeklyRosterOverrides:', { teamId });

    try {
      const response = await axiosInstance.get<any[]>(url);
      pdebug('getWeeklyRosterOverrides success:', response.data);
      return response.data || [];
    } catch (err: any) {
      pdebugError('getWeeklyRosterOverrides failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Delete a weekly roster override, reverting to previous week's roster.
   * DELETE /api/admin/operations/work-area-teams/{id}/roster-weeks/{week}/
   */
  static async deleteWeeklyRosterOverride(teamId: number, week: string): Promise<void> {
    const url = `${BASE}work-area-teams/${teamId}/roster-weeks/${week}/`;
    pdebug('deleteWeeklyRosterOverride:', { teamId, week });

    try {
      await axiosInstance.delete(url);
      pdebug('deleteWeeklyRosterOverride success');
    } catch (err: any) {
      pdebugError('deleteWeeklyRosterOverride failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  // ============================================================================
  // Job Shift Assignment Methods
  // ============================================================================

  /**
   * Assigns an employee to a job shift.
   * CANONICAL: POST /api/admin/operations/job-shifts/{shift_id}/assign-employee/
   */
  static async assignEmployeeToShift(shiftId: number, employeeId: number): Promise<EmployeeAssignment> {
    const url = `${BASE}job-shifts/${shiftId}/assign-employee/`;
    pdebug('assignEmployeeToShift:', { shiftId, employeeId, url });

    const response = await axiosInstance.post<any>(url, { employee_id: employeeId });

    pdebug('assignEmployeeToShift success:', response.data);
    return {
      id: response.data?.id || 0,
      employee_id: employeeId,
      employee_name: response.data?.employee_name || '',
      shift_id: shiftId,
    };
  }

  /**
   * Unassigns an employee from a job shift.
   * CANONICAL: POST /api/admin/operations/job-shifts/{shift_id}/unassign-employee/
   */
  static async unassignEmployeeFromShift(shiftId: number, employeeId: number): Promise<void> {
    pdebug('unassignEmployeeFromShift:', { shiftId, employeeId });

    await axiosInstance.post(`${BASE}job-shifts/${shiftId}/unassign-employee/`, {
      employee_id: employeeId,
    });

    pdebug('unassignEmployeeFromShift success');
  }

  /**
   * Reassigns an employee from one job shift to another.
   * CANONICAL: POST /api/admin/operations/job-shifts/{target_shift_id}/reassign-employee/
   */
  static async reassignEmployeeToShift(
    targetShiftId: number,
    employeeId: number,
    sourceShiftId: number
  ): Promise<EmployeeAssignment> {
    const url = `${BASE}job-shifts/${targetShiftId}/reassign-employee/`;
    pdebug('reassignEmployeeToShift:', { targetShiftId, employeeId, sourceShiftId, url });

    const response = await axiosInstance.post<any>(url, {
      employee_id: employeeId,
      from_shift_id: sourceShiftId,
    });

    pdebug('reassignEmployeeToShift success:', response.data);
    return {
      id: response.data?.id || 0,
      employee_id: employeeId,
      employee_name: response.data?.employee_name || '',
      shift_id: targetShiftId,
    };
  }

  static async fetchAvailableEmployees(shiftId: number): Promise<any[]> {
    try {
      const response = await axiosInstance.get<any[]>(
        `/api/admin/shifts/${shiftId}/available-employees/`
      );
      return response.data;
    } catch (err) {
      pdebugWarn('Available employees endpoint not available:', err);
      return [];
    }
  }

  // ========== Asset Methods ==========

  /**
   * Fetches all assets for a team (Team-Kit).
   * GET /api/admin/work-area-teams/{id}/assets/
   */
  static async fetchTeamAssets(teamId: number): Promise<Asset[]> {
    try {
      const response = await axiosInstance.get<any>(
        `/api/admin/work-area-teams/${teamId}/assets/`
      );
      pdebug('fetchTeamAssets response:', response.data);
      return parseList<Asset>(response.data, ['assets', 'results']);
    } catch (err) {
      pdebugWarn('Team assets endpoint not available:', err);
      return [];
    }
  }

  /**
   * Fetches assigned assets for a shift.
   * GET /api/admin/shifts/{id}/assets/
   */
  static async fetchShiftAssets(shiftId: number): Promise<Asset[]> {
    try {
      const response = await axiosInstance.get<any>(
        `/api/admin/shifts/${shiftId}/assets/`
      );
      pdebug('fetchShiftAssets response:', response.data);
      return parseList<Asset>(response.data, ['assets', 'results']);
    } catch (err) {
      pdebugWarn('Shift assets endpoint not available:', err);
      return [];
    }
  }

  /**
   * Fetches available assets for a job shift.
   * PRIMARY: GET /api/admin/operations/job-shifts/{shift_id}/available-assets/
   *
   * Response: { assets: [{ id, asset_name, asset_type, is_available, is_assigned, reasons }] }
   */
  static async fetchAvailableAssets(shiftId: number): Promise<AvailableAsset[]> {
    try {
      const response = await axiosInstance.get<any>(
        `${BASE}job-shifts/${shiftId}/available-assets/`
      );
      pdebug('fetchAvailableAssets response:', response.data);

      // Map response to AvailableAsset interface
      const assets = parseList<any>(response.data, ['assets']);
      return assets.map((a: any) => ({
        id: a.id,
        name: a.asset_name || a.name,
        code: a.code,
        asset_type: a.asset_type,
        category: a.category,
        license_plate: a.license_plate,
        status: a.status,
        is_active: a.is_active,
        availability_status: a.is_available === false ? 'busy' : 'available',
        busy_reason: a.reasons?.join(', ') || undefined,
        is_assigned: a.is_assigned,
      }));
    } catch (err) {
      pdebugWarn('Available assets endpoint not available:', err);
      return [];
    }
  }

  /**
   * Assigns an asset to a job shift.
   * CANONICAL: POST /api/admin/operations/job-shifts/{shift_id}/assign-asset/
   */
  static async assignAssetToShift(
    shiftId: number,
    assetId: number
  ): Promise<AssetAssignment> {
    const url = `${BASE}job-shifts/${shiftId}/assign-asset/`;
    pdebug('assignAssetToShift:', { shiftId, assetId });

    const response = await axiosInstance.post<any>(url, { asset_id: assetId });
    pdebug('assignAssetToShift success:', response.data);
    return {
      id: response.data?.id || 0,
      asset_id: assetId,
      asset_name: response.data?.asset_name || '',
      shift_id: shiftId,
    };
  }

  /**
   * Unassigns an asset from a job shift.
   * CANONICAL: POST /api/admin/operations/job-shifts/{shift_id}/unassign-asset/
   */
  static async unassignAssetFromShift(
    shiftId: number,
    assetId: number
  ): Promise<void> {
    pdebug('unassignAssetFromShift:', { shiftId, assetId });

    await axiosInstance.post(
      `${BASE}job-shifts/${shiftId}/unassign-asset/`,
      { asset_id: assetId }
    );

    pdebug('unassignAssetFromShift success');
  }

  static async createTeam(payload: {
    name: string;
    work_area: number;
    color?: string;
    sort_order?: number;
    is_active?: boolean;
  }): Promise<LaneTeam> {
    const response = await axiosInstance.post<LaneTeam>(TEAMS_ENDPOINT, payload);
    return response.data;
  }

  static async updateTeam(
    teamId: number,
    payload: {
      name?: string;
      color?: string;
      sort_order?: number;
      is_active?: boolean;
    }
  ): Promise<LaneTeam> {
    const response = await axiosInstance.patch<LaneTeam>(`${TEAMS_ENDPOINT}${teamId}/`, payload);
    return response.data;
  }

  static async deleteTeam(teamId: number): Promise<void> {
    await axiosInstance.delete(`${TEAMS_ENDPOINT}${teamId}/`);
  }

  /**
   * Creates a new job directly from the Plantafel.
   *
   * @param payload - Job creation data
   * @returns The created Shift
   */
  static async createJob(payload: {
    project_id: number;
    title: string;
    description?: string;
    priority?: 'NORMAL' | 'URGENT' | 'EMERGENCY';
    work_area_team_id: number;
    mode: 'FULL_DAY' | 'CUSTOM';
    start_date: string;
    schedule?: boolean;
  }): Promise<Shift> {
    pdebug('createJob called:', payload);

    const requestPayload = {
      ...payload,
      schedule: payload.schedule ?? true,
    };

    try {
      const response = await axiosInstance.post<Shift>(`${BASE}jobs/`, requestPayload);
      pdebug('createJob success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('createJob failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      throw err;
    }
  }

  static async createShiftFromTask(
    taskId: number,
    startTime: string,
    workAreaId: number,
    durationHours: number = 2
  ): Promise<Shift> {
    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

    const payload: ShiftCreatePayload = {
      start_time: formatLocalDateTime(startDate),
      end_time: formatLocalDateTime(endDate),
      work_area: workAreaId,
      employees_needed: 1,
      crm_task: taskId,
    };

    return ShiftService.createShift(payload);
  }

  static async moveShift(
    shiftId: number,
    dateStr: string,
    workAreaId: number
  ): Promise<Shift> {
    const shift = await axiosInstance.get<Shift>(`/api/shifts/${shiftId}/`);
    const currentStart = new Date(shift.data.start_time);
    const currentEnd = new Date(shift.data.end_time);
    const startHours = currentStart.getHours().toString().padStart(2, '0');
    const startMins = currentStart.getMinutes().toString().padStart(2, '0');
    const endHours = currentEnd.getHours().toString().padStart(2, '0');
    const endMins = currentEnd.getMinutes().toString().padStart(2, '0');

    return ShiftService.updateShift(shiftId, {
      start_time: `${dateStr}T${startHours}:${startMins}:00`,
      end_time: `${dateStr}T${endHours}:${endMins}:00`,
      work_area: workAreaId,
      employees_needed: shift.data.employees_needed || 1,
    });
  }

  static async deleteShift(shiftId: number): Promise<void> {
    await ShiftService.deleteShift(shiftId, 'this');
  }

  /**
   * Reschedule a job shift to a different team (keeping times).
   * POST /api/admin/operations/job-shifts/{shift_id}/reschedule/
   */
  static async rescheduleShift(
    shiftId: number,
    workAreaTeamId: number,
    mode: 'MOVE_ONLY' | 'FULL_DAY' = 'MOVE_ONLY'
  ): Promise<Shift> {
    pdebug('rescheduleShift:', { shiftId, workAreaTeamId, mode });

    const response = await axiosInstance.post<Shift>(
      `${BASE}job-shifts/${shiftId}/reschedule/`,
      {
        work_area_team_id: workAreaTeamId,
        mode,
      }
    );

    pdebug('rescheduleShift success:', response.data);
    return response.data;
  }

  /**
   * Unschedule a job shift (move back to backlog).
   * POST /api/admin/operations/job-shifts/{shift_id}/unschedule/
   */
  static async unscheduleShift(shiftId: number): Promise<void> {
    pdebug('unscheduleShift:', { shiftId });

    await axiosInstance.post(`${BASE}job-shifts/${shiftId}/unschedule/`);

    pdebug('unscheduleShift success');
  }

  /**
   * Creates a new project.
   * POST /api/admin/operations/projects/
   */
  static async createProject(payload: {
    partner_id: number;
    name: string;
    planned_start?: string;
    planned_end?: string;
    status?: string;
  }): Promise<ProjectTimelineItem> {
    pdebug('createProject:', payload);

    const requestPayload = {
      ...payload,
      status: payload.status ?? 'IN_EXECUTION',
    };

    try {
      const response = await axiosInstance.post<ProjectTimelineItem>(`${BASE}projects/`, requestPayload);
      pdebug('createProject success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('createProject failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      throw err;
    }
  }

  // ============================================================================
  // FE-OPS-R24-3: Project Dossier API Methods
  // ============================================================================

  /**
   * Fetches project dossier (overview + KPIs).
   * GET /api/admin/operations/projects/{id}/dossier/
   */
  static async fetchProjectDossier(projectId: number): Promise<ProjectDossier> {
    const url = `${BASE}projects/${projectId}/dossier/`;
    pdebug('fetchProjectDossier:', { projectId });

    try {
      const response = await axiosInstance.get<ProjectDossier>(url);
      pdebug('fetchProjectDossier success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('fetchProjectDossier failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Fetches job shifts for a project within a date range.
   * GET /api/admin/operations/projects/{id}/job-shifts/?from=YYYY-MM-DD&to=YYYY-MM-DD
   */
  static async fetchProjectJobShifts(
    projectId: number,
    fromDate?: string,
    toDate?: string
  ): Promise<ProjectJobShift[]> {
    let url = `${BASE}projects/${projectId}/job-shifts/`;
    const params: string[] = [];
    if (fromDate) params.push(`from=${fromDate}`);
    if (toDate) params.push(`to=${toDate}`);
    if (params.length > 0) url += `?${params.join('&')}`;

    pdebug('fetchProjectJobShifts:', { projectId, fromDate, toDate });

    try {
      const response = await axiosInstance.get<any>(url);
      pdebug('fetchProjectJobShifts raw:', response.data);

      let jobs: ProjectJobShift[];
      if (Array.isArray(response.data)) {
        jobs = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        jobs = response.data.results;
      } else if (response.data?.jobs && Array.isArray(response.data.jobs)) {
        jobs = response.data.jobs;
      } else {
        jobs = [];
      }

      pdebug('fetchProjectJobShifts result:', jobs.length, 'jobs');
      return jobs;
    } catch (err: any) {
      pdebugError('fetchProjectJobShifts failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  // ============================================================================
  // FE-OPS-R25-1: Milestones API Methods
  // ============================================================================

  /**
   * Fetches milestones for a project with tolerant parsing.
   * GET /api/admin/operations/projects/{projectId}/milestones/
   * FE-OPS-R25-HF-A: Canonical path + tolerant parsing
   * FE-OPS-R26-2: Added include_jobs parameter for job display
   * FE-OPS-R27-2: Added include_positions parameter for positions display
   */
  static async fetchProjectMilestones(projectId: number, includeTasks: boolean = true, includePositions: boolean = true): Promise<ProjectMilestone[]> {
    const url = `${BASE}projects/${projectId}/milestones/`;
    const params = {
      include_tasks: includeTasks ? 1 : 0,
      include_jobs: includeTasks ? 1 : 0,
      include_positions: includePositions ? 1 : 0,
    };
    pdebug('fetchProjectMilestones:', { projectId, includeTasks, includePositions, url });

    try {
      const response = await axiosInstance.get<any>(url, { params });
      pdebug('fetchProjectMilestones raw:', response.data);

      // Tolerant list parsing
      let rawMilestones: any[];
      if (Array.isArray(response.data)) {
        rawMilestones = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        rawMilestones = response.data.results;
      } else if (response.data?.milestones && Array.isArray(response.data.milestones)) {
        rawMilestones = response.data.milestones;
      } else {
        rawMilestones = [];
      }

      // FE-OPS-R25-HF-A: Tolerant parsing for milestone fields
      // FE-OPS-R26-2: Added jobs parsing
      // FE-OPS-R27-2: Added positions parsing
      const milestones: ProjectMilestone[] = rawMilestones.map((m: any) => {
        // Parse jobs array with tolerant field names
        const rawJobs = m.jobs || m.linked_jobs || [];
        const jobs: MilestoneJob[] = rawJobs.map((j: any) => ({
          id: j.id,
          title: j.title || j.name || '',
          status: j.status || 'OPEN',
          checklist_total: j.checklist_total ?? j.checklist_progress?.total ?? 0,
          checklist_done: j.checklist_done ?? j.checklist_progress?.done ?? 0,
          start_time: j.start_time,
          shift_id: j.shift_id ?? j.scheduled_shift_id,
        }));

        // FE-OPS-R27-2: Parse positions array
        const rawPositions = m.positions || m.line_items || [];
        const positions: MilestonePosition[] = rawPositions.map((p: any) => ({
          id: p.id,
          name: p.name || p.title || p.description || '',
          amount: p.amount ?? p.amount_net ?? p.total_net ?? 0,
        }));

        return {
          id: m.id,
          label: m.label || m.name || '',
          percent: m.percent ?? m.percentage ?? 0,
          status: m.status || 'OPEN',
          computed: {
            // Tolerant parsing: try multiple field names
            percent_done: m.computed?.percent_done ?? m.progress_percent ?? m.percent_done ?? 0,
            tasks_total: m.computed?.tasks_total ?? m.checklist_total ?? m.tasks_total ?? 0,
            tasks_done: m.computed?.tasks_done ?? m.checklist_done ?? m.tasks_done ?? 0,
          },
          tasks: m.tasks || m.subtasks || [],
          jobs,
          positions,
          positions_total_net: m.positions_total_net ?? positions.reduce((sum, p) => sum + p.amount, 0),
          invoice_id: m.invoice_id,
          invoice_number: m.invoice_number,
          amount: m.amount ?? m.value ?? undefined,
          due_date: m.due_date,
        };
      });

      pdebug('fetchProjectMilestones result:', milestones.length, 'milestones');
      return milestones;
    } catch (err: any) {
      pdebugError('fetchProjectMilestones failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * FE-OPS-R27-3: Fetch project milestones with full metadata (offer_id, payment_plan_id)
   * Used in ApplyTemplateModal to display billing milestones from the linked Offer.
   * GET /api/admin/operations/projects/{id}/milestones/?include_positions=1
   */
  static async fetchProjectMilestonesWithMeta(
    projectId: number,
    includePositions: boolean = true
  ): Promise<ProjectMilestonesResponse> {
    const url = `${BASE}projects/${projectId}/milestones/`;
    const params = {
      include_positions: includePositions ? 1 : 0,
    };
    pdebug('fetchProjectMilestonesWithMeta:', { projectId, includePositions, url });

    try {
      const response = await axiosInstance.get<any>(url, { params });
      pdebug('fetchProjectMilestonesWithMeta raw:', response.data);

      // Parse milestones array
      let rawMilestones: any[];
      if (Array.isArray(response.data)) {
        rawMilestones = response.data;
      } else if (response.data?.milestones && Array.isArray(response.data.milestones)) {
        rawMilestones = response.data.milestones;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        rawMilestones = response.data.results;
      } else {
        rawMilestones = [];
      }

      // Parse each milestone with positions
      const milestones: ProjectMilestone[] = rawMilestones.map((m: any) => {
        const rawPositions = m.positions || m.line_items || [];
        const positions: MilestonePosition[] = rawPositions.map((p: any) => ({
          id: p.id,
          name: p.name || p.title || p.description || '',
          amount: p.amount ?? p.amount_net ?? p.total_net ?? 0,
        }));

        return {
          id: m.id,
          label: m.label || m.name || '',
          percent: m.percent ?? m.percentage ?? 0,
          status: m.status || 'OPEN',
          positions,
          positions_total_net: m.positions_total_net ?? positions.reduce((sum, p) => sum + p.amount, 0),
        };
      });

      // Build full response with metadata
      const result: ProjectMilestonesResponse = {
        project_id: response.data?.project_id ?? projectId,
        offer_id: response.data?.offer_id ?? null,
        payment_plan_id: response.data?.payment_plan_id ?? null,
        has_payment_plan: response.data?.has_payment_plan ?? milestones.length > 0,
        milestones,
        meta: response.data?.meta,
      };

      pdebug('fetchProjectMilestonesWithMeta result:', result);
      return result;
    } catch (err: any) {
      pdebugError('fetchProjectMilestonesWithMeta failed:', err?.response?.status, err?.message);
      return {
        project_id: projectId,
        offer_id: null,
        payment_plan_id: null,
        has_payment_plan: false,
        milestones: [],
      };
    }
  }

  // ============================================================================
  // FE-OPS-R28-3: Generate Jobs from Milestone Positions
  // CANONICAL URL - NO tolerant parsing
  // ============================================================================

  /**
   * Generate jobs from positions assigned to a milestone.
   * POST /api/admin/operations/projects/{projectId}/milestones/{paymentMilestoneId}/generate-jobs/
   */
  static async generateJobsFromMilestone(
    projectId: number,
    paymentMilestoneId: number,
    payload?: GenerateJobsPayload
  ): Promise<GenerateJobsResponse> {
    const url = `${BASE}projects/${projectId}/milestones/${paymentMilestoneId}/generate-jobs/`;
    pdebug('generateJobsFromMilestone:', { projectId, paymentMilestoneId, payload, url });

    const response = await axiosInstance.post<GenerateJobsResponse>(url, payload || { mode: 'APPEND' });
    pdebug('generateJobsFromMilestone result:', response.data);
    return response.data;
  }

  // ============================================================================
  // FE-OPS-R24-1: Sites CRUD API Methods
  // ============================================================================

  /**
   * Fetches all sites for a project.
   * GET /api/admin/operations/projects/{id}/sites/
   */
  static async fetchProjectSites(projectId: number): Promise<ProjectSite[]> {
    const url = `${BASE}projects/${projectId}/sites/`;
    pdebug('fetchProjectSites:', { projectId });

    try {
      const response = await axiosInstance.get<any>(url);
      pdebug('fetchProjectSites raw:', response.data);

      let sites: ProjectSite[];
      if (Array.isArray(response.data)) {
        sites = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        sites = response.data.results;
      } else if (response.data?.sites && Array.isArray(response.data.sites)) {
        sites = response.data.sites;
      } else {
        sites = [];
      }

      pdebug('fetchProjectSites result:', sites.length, 'sites');
      return sites;
    } catch (err: any) {
      pdebugError('fetchProjectSites failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Creates a new site for a project.
   * POST /api/admin/operations/projects/{id}/sites/
   */
  static async createProjectSite(projectId: number, payload: CreateSitePayload): Promise<ProjectSite> {
    const url = `${BASE}projects/${projectId}/sites/`;
    pdebug('createProjectSite:', { projectId, payload });

    try {
      const response = await axiosInstance.post<ProjectSite>(url, payload);
      pdebug('createProjectSite success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('createProjectSite failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Updates a site.
   * PATCH /api/admin/operations/project-sites/{id}/
   */
  static async updateProjectSite(siteId: number, payload: UpdateSitePayload): Promise<ProjectSite> {
    const url = `${BASE}project-sites/${siteId}/`;
    pdebug('updateProjectSite:', { siteId, payload });

    try {
      const response = await axiosInstance.patch<ProjectSite>(url, payload);
      pdebug('updateProjectSite success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('updateProjectSite failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Deletes a site (soft delete).
   * DELETE /api/admin/operations/project-sites/{id}/
   */
  static async deleteProjectSite(siteId: number): Promise<void> {
    const url = `${BASE}project-sites/${siteId}/`;
    pdebug('deleteProjectSite:', { siteId });

    try {
      await axiosInstance.delete(url);
      pdebug('deleteProjectSite success');
    } catch (err: any) {
      pdebugError('deleteProjectSite failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Updates job execution site.
   * PATCH /api/admin/operations/jobs/{crm_task_id}/
   */
  static async updateJobSite(crmTaskId: number, executionSiteId: number | null): Promise<void> {
    const url = `${BASE}jobs/${crmTaskId}/`;
    pdebug('updateJobSite:', { crmTaskId, executionSiteId });

    try {
      await axiosInstance.patch(url, {
        execution_site_id: executionSiteId,
      });
      pdebug('updateJobSite success');
    } catch (err: any) {
      pdebugError('updateJobSite failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * FE-OPS-R26-3: Updates the billing milestone for a job.
   * PATCH /api/admin/operations/jobs/{crmTaskId}/
   */
  static async updateJobMilestone(crmTaskId: number, paymentMilestoneId: number | null): Promise<void> {
    const url = `${BASE}jobs/${crmTaskId}/`;
    pdebug('updateJobMilestone:', { crmTaskId, paymentMilestoneId });

    try {
      await axiosInstance.patch(url, {
        payment_milestone_id: paymentMilestoneId,
      });
      pdebug('updateJobMilestone success');
    } catch (err: any) {
      pdebugError('updateJobMilestone failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Updates shift start and end time.
   * PATCH /api/admin/shifts/{id}/
   */
  static async updateShiftTime(
    shiftId: number,
    startTime: string,
    endTime: string
  ): Promise<Shift> {
    pdebug('updateShiftTime:', { shiftId, startTime, endTime });

    try {
      const response = await axiosInstance.patch<Shift>(
        `/api/admin/shifts/${shiftId}/`,
        {
          start_time: startTime,
          end_time: endTime,
        }
      );
      pdebug('updateShiftTime success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('updateShiftTime failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      throw err;
    }
  }

  /**
   * Reschedules a project to new dates.
   * POST /api/admin/operations/projects/{id}/reschedule/
   */
  static async rescheduleProject(
    projectId: number,
    plannedStart: string,
    plannedEnd: string
  ): Promise<ProjectTimelineItem> {
    pdebug('rescheduleProject:', { projectId, plannedStart, plannedEnd });

    try {
      const response = await axiosInstance.post<ProjectTimelineItem>(
        `${BASE}projects/${projectId}/reschedule/`,
        {
          planned_start: plannedStart,
          planned_end: plannedEnd,
        }
      );
      pdebug('rescheduleProject success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('rescheduleProject failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      throw err;
    }
  }

  // ============================================================================
  // FE-OPS-R25-2: Operations Templates API Methods
  // ============================================================================

  /**
   * Fetches all available operations templates.
   * GET /api/admin/operations/templates/
   */
  static async fetchTemplates(): Promise<OperationsTemplate[]> {
    pdebug('fetchTemplates');

    try {
      const response = await axiosInstance.get<any>(`${BASE}templates/`);
      pdebug('fetchTemplates raw:', response.data);

      const templates = parseList<OperationsTemplate>(response.data, ['results', 'templates']);
      pdebug('fetchTemplates result:', templates.length, 'templates');
      return templates;
    } catch (err: any) {
      pdebugError('fetchTemplates failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Fetches a single template with full details.
   * GET /api/admin/operations/templates/{id}/
   */
  static async fetchTemplate(templateId: number): Promise<OperationsTemplate | null> {
    pdebug('fetchTemplate:', { templateId });

    try {
      const response = await axiosInstance.get<OperationsTemplate>(`${BASE}templates/${templateId}/`);
      pdebug('fetchTemplate success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('fetchTemplate failed:', err?.response?.status, err?.message);
      return null;
    }
  }

  /**
   * Applies a template to a project, creating milestones and jobs.
   * POST /api/admin/operations/projects/{projectId}/apply-template/
   */
  static async applyTemplate(projectId: number, payload: ApplyTemplatePayload): Promise<ApplyTemplateResult> {
    pdebug('applyTemplate:', { projectId, payload });

    try {
      const response = await axiosInstance.post<ApplyTemplateResult>(
        `${BASE}projects/${projectId}/apply-template/`,
        payload
      );
      pdebug('applyTemplate success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('applyTemplate failed:', {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      throw err;
    }
  }

  // ============================================================================
  // FE-OPS-R25-3: Job Detail & Checklist API Methods
  // ============================================================================

  /**
   * Fetches jobs by payment milestone ID.
   * GET /api/admin/operations/jobs/?payment_milestone_id={milestoneId}
   */
  static async fetchJobsByMilestone(paymentMilestoneId: number): Promise<JobDetail[]> {
    pdebug('fetchJobsByMilestone:', { paymentMilestoneId });

    try {
      const response = await axiosInstance.get<any>(`${BASE}jobs/`, {
        params: { payment_milestone_id: paymentMilestoneId }
      });
      pdebug('fetchJobsByMilestone raw:', response.data);

      const jobs = parseList<JobDetail>(response.data, ['results', 'jobs', 'tasks']);
      pdebug('fetchJobsByMilestone result:', jobs.length, 'jobs');
      return jobs;
    } catch (err: any) {
      pdebugError('fetchJobsByMilestone failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Fetches all jobs for an offer.
   * GET /api/admin/operations/jobs/?offer_id={offerId}
   */
  static async fetchJobsByOffer(offerId: number): Promise<JobDetail[]> {
    pdebug('fetchJobsByOffer:', { offerId });

    try {
      const response = await axiosInstance.get<any>(`${BASE}jobs/`, {
        params: { offer_id: offerId }
      });
      pdebug('fetchJobsByOffer raw:', response.data);

      const jobs = parseList<JobDetail>(response.data, ['results', 'jobs', 'tasks']);
      pdebug('fetchJobsByOffer result:', jobs.length, 'jobs');
      return jobs;
    } catch (err: any) {
      pdebugError('fetchJobsByOffer failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Fetches job details including milestone info.
   * GET /api/admin/operations/jobs/{taskId}/
   */
  static async fetchJobDetail(taskId: number): Promise<JobDetail | null> {
    pdebug('fetchJobDetail:', { taskId });

    try {
      const response = await axiosInstance.get<{ task: JobDetail } | JobDetail>(`${BASE}jobs/${taskId}/`);
      pdebug('fetchJobDetail success:', response.data);
      // Handle both wrapped { task: {...} } and unwrapped {...} response formats
      const data = (response.data as any)?.task || response.data;
      return data;
    } catch (err: any) {
      pdebugError('fetchJobDetail failed:', err?.response?.status, err?.message);
      return null;
    }
  }

  /**
   * Deletes a job.
   * DELETE /api/admin/operations/jobs/{taskId}/
   */
  static async deleteJob(taskId: number): Promise<void> {
    pdebug('deleteJob:', { taskId });

    try {
      await axiosInstance.delete(`${BASE}jobs/${taskId}/`);
      pdebug('deleteJob success');
    } catch (err: any) {
      pdebugError('deleteJob failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Fetches checklist items for a job.
   * GET /api/admin/operations/jobs/{taskId}/checklist/
   */
  static async fetchJobChecklist(taskId: number): Promise<ChecklistItem[]> {
    pdebug('fetchJobChecklist:', { taskId });

    try {
      const response = await axiosInstance.get<any>(`${BASE}jobs/${taskId}/checklist/`);
      pdebug('fetchJobChecklist raw:', response.data);

      const items = parseList<ChecklistItem>(response.data, ['results', 'items', 'checklist']);
      pdebug('fetchJobChecklist result:', items.length, 'items');
      return items;
    } catch (err: any) {
      pdebugError('fetchJobChecklist failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Creates a new checklist item for a job.
   * POST /api/admin/operations/jobs/{taskId}/checklist/
   */
  static async createChecklistItem(taskId: number, payload: CreateChecklistItemPayload): Promise<ChecklistItem> {
    pdebug('createChecklistItem:', { taskId, payload });

    try {
      const response = await axiosInstance.post<ChecklistItem>(
        `${BASE}jobs/${taskId}/checklist/`,
        payload
      );
      pdebug('createChecklistItem success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('createChecklistItem failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Updates a checklist item.
   * FE-OPS-R25-HF-A: Canonical path
   * PATCH /api/admin/operations/job-checklist-items/{itemId}/
   */
  static async updateChecklistItem(
    taskId: number,
    itemId: number,
    payload: UpdateChecklistItemPayload
  ): Promise<ChecklistItem> {
    // FE-OPS-R25-HF-A: Use canonical path for PATCH
    const url = `${BASE}job-checklist-items/${itemId}/`;
    pdebug('updateChecklistItem:', { taskId, itemId, payload, url });

    try {
      const response = await axiosInstance.patch<ChecklistItem>(url, payload);
      pdebug('updateChecklistItem success:', response.data);
      return response.data;
    } catch (err: any) {
      pdebugError('updateChecklistItem failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Deletes a checklist item.
   * FE-OPS-R25-HF-A: Canonical path
   * DELETE /api/admin/operations/job-checklist-items/{itemId}/
   */
  static async deleteChecklistItem(taskId: number, itemId: number): Promise<void> {
    // FE-OPS-R25-HF-A: Use canonical path for DELETE
    const url = `${BASE}job-checklist-items/${itemId}/`;
    pdebug('deleteChecklistItem:', { taskId, itemId, url });

    try {
      await axiosInstance.delete(url);
      pdebug('deleteChecklistItem success');
    } catch (err: any) {
      pdebugError('deleteChecklistItem failed:', err?.response?.status, err?.message);
      throw err;
    }
  }

  /**
   * Toggles a checklist item's done status.
   * Convenience method that calls updateChecklistItem.
   */
  static async toggleChecklistItem(taskId: number, itemId: number, isDone: boolean): Promise<ChecklistItem> {
    return this.updateChecklistItem(taskId, itemId, { is_done: isDone });
  }

  static getDayColumns(weekStart: Date, numDays: number = 7): { date: Date; dateStr: string; label: string }[] {
    const days: { date: Date; dateStr: string; label: string }[] = [];
    const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

    for (let i = 0; i < numDays; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);

      // Use utility for consistent local date formatting
      const dateStr = formatLocalDate(date);
      const dayNum = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const dayName = dayNames[date.getDay()];

      days.push({
        date,
        dateStr,
        label: `${dayName}, ${dayNum}.${month}.`,
      });
    }

    return days;
  }

  static getShiftsForCell(
    shifts: Shift[],
    dateStr: string,
    workAreaId: number
  ): Shift[] {
    return shifts.filter((shift) => {
      const shiftDate = shift.start_time.split('T')[0];
      const shiftWaId = typeof shift.work_area === 'number'
        ? shift.work_area
        : parseInt(String(shift.work_area), 10);
      return shiftDate === dateStr && shiftWaId === workAreaId;
    });
  }

  static formatShiftTime(shift: Shift): string {
    const start = new Date(shift.start_time);
    const end = new Date(shift.end_time);
    const startStr = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
    const endStr = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`;
    return `${startStr} - ${endStr}`;
  }

  static getStatusColor(status: string): { bg: string; text: string; border: string } {
    switch (status) {
      case 'OPEN':
        return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' };
      case 'IN_PROGRESS':
        return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
      case 'DONE':
        return { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' };
      case 'CANCELLED':
        return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
    }
  }

  static getPriorityColor(priority: string): { bg: string; text: string } {
    switch (priority) {
      case 'HIGH':
        return { bg: '#fee2e2', text: '#991b1b' };
      case 'NORMAL':
        return { bg: '#e0e7ff', text: '#3730a3' };
      case 'LOW':
        return { bg: '#f3f4f6', text: '#374151' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static getDayIndex(dateStr: string, weekStart: Date): number {
    const date = new Date(dateStr);
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.floor((date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculates shift positions for the Gantt chart.
   * Uses work window mapping: positions are based on the work window (e.g., 08:00-17:00)
   * rather than full 24-hour days. This ensures shifts appear correctly within the visible
   * work hours grid.
   *
   * IMPORTANT: For team rows, we filter by work_area_team_id (not work_area).
   * - Job shifts (is_job_shift=true): Must have work_area_team_id matching the team
   * - Regular shifts: Matched by work_area (to the parent WorkArea of the team)
   *
   * @param teamId - The work_area_team_id to filter by (NOT the work_area_id)
   * @param parentWorkAreaId - The parent work_area_id (for regular shift matching)
   */
  static getShiftsForTeam(
    shifts: Shift[],
    teamId: number,
    parentWorkAreaId: number,
    weekStart: Date,
    taskMap: Map<number, CrmTask>,
    totalWidth: number,
    settings?: OperationsSettings,
    includeRegularShifts: boolean = false,
    viewMode: 'week' | 'month' | 'year' | 'custom' = 'week',
    numDays: number = 7
  ): { positions: GanttShiftPosition[]; lanes: number; invalidJobShifts: Shift[] } {
    const dayStartMinutes = settings?.day_start_minutes ?? 6 * 60;   // 06:00
    const dayEndMinutes = settings?.day_end_minutes ?? 18 * 60;      // 18:00
    const workWindowMinutes = dayEndMinutes - dayStartMinutes;       // 12 hours = 720 minutes
    const colWidth = totalWidth / numDays;                            // Width per day column

    const invalidJobShifts: Shift[] = [];

    pdebug(`getShiftsForTeam: Filtering for teamId=${teamId}, parentWorkAreaId=${parentWorkAreaId}, total shifts=${shifts.length}`);

    // Filter shifts for this team row
    const teamShifts = shifts.filter((shift) => {
      // Normalize is_job_shift (backend might send true, 1, "true", etc.)
      // Also treat shifts with work_area_team_id as job shifts (they belong to a specific team)
      const shiftTeamIdRaw = shift.work_area_team_id ??
                             (shift as any).work_area_team ??
                             (shift as any).team_id;
      const hasTeamAssignment = shiftTeamIdRaw !== null && shiftTeamIdRaw !== undefined;

      const isJobShift = shift.is_job_shift === true ||
                         (shift as any).is_job_shift === 1 ||
                         (shift as any).is_job_shift === 'true' ||
                         // Fallback: if shift has crm_task, consider it a job shift
                         (shift.crm_task !== null && shift.crm_task !== undefined) ||
                         // Fallback: if shift has work_area_team_id, it belongs to a specific team
                         hasTeamAssignment;

      // Use the already extracted team ID
      const shiftTeamId = shiftTeamIdRaw;

      // Job shifts: must match by work_area_team_id or by work_area (for WAs without teams)
      if (isJobShift) {
        // Get shift's work_area
        const shiftWaId = typeof shift.work_area === 'number'
          ? shift.work_area
          : parseInt(String(shift.work_area), 10);

        // Special case: WA without teams (teamId === parentWorkAreaId)
        // In this case, we show ALL job shifts for this work_area, regardless of their team_id
        if (teamId === parentWorkAreaId) {
          const matchesByWa = shiftWaId === parentWorkAreaId;
          if (matchesByWa) {
            pdebug(`getShiftsForTeam: Shift ${shift.id}: isJobShift=true, WA without teams, matches by work_area=${shiftWaId}`);
            return true;
          }
        }

        // Normal case: WA with teams - must match by team_id
        if (shiftTeamId === null || shiftTeamId === undefined) {
          invalidJobShifts.push(shift);
          pdebugWarn('INVALID job shift (missing team_id):', {
            shiftId: shift.id,
            crmTask: shift.crm_task,
            startTime: shift.start_time,
            work_area: shiftWaId,
            rawShift: { is_job_shift: shift.is_job_shift, work_area_team_id: shift.work_area_team_id },
          });
          return false; // Don't render in any row
        }

        const matchesByTeam = shiftTeamId === teamId;
        pdebug(`getShiftsForTeam: Shift ${shift.id}: isJobShift=true, shiftTeamId=${shiftTeamId}, teamId=${teamId}, matches=${matchesByTeam}`);
        return matchesByTeam;
      }

      // Regular shifts (no team assignment): match by work_area
      if (includeRegularShifts) {
        const shiftWaId = typeof shift.work_area === 'number'
          ? shift.work_area
          : parseInt(String(shift.work_area), 10);
        return shiftWaId === parentWorkAreaId;
      }

      return false;
    });

    pdebug(`getShiftsForTeam: Result: ${teamShifts.length} shifts matched for teamId=${teamId}`);

    const positions: GanttShiftPosition[] = [];

    // For year view, each column is a week (7 days), not a single day
    const msPerColumn = viewMode === 'year' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    const minutesPerColumn = viewMode === 'year' ? 7 * MINUTES_PER_DAY : MINUTES_PER_DAY;

    const rangeStartMs = new Date(weekStart).setHours(0, 0, 0, 0);
    const rangeEndMs = rangeStartMs + numDays * msPerColumn;
    const totalMinutesInRange = numDays * minutesPerColumn;

    teamShifts.forEach((shift) => {
      const shiftStartMs = new Date(shift.start_time).getTime();
      const shiftEndMs = new Date(shift.end_time).getTime();

      if (shiftEndMs <= rangeStartMs || shiftStartMs >= rangeEndMs) {
        return;
      }

      const originalStartMinutes = (shiftStartMs - rangeStartMs) / (1000 * 60);
      const originalEndMinutes = (shiftEndMs - rangeStartMs) / (1000 * 60);

      const clampedLeft = shiftStartMs < rangeStartMs;
      const clampedRight = shiftEndMs > rangeEndMs;

      const clampedStartMinutes = Math.max(0, originalStartMinutes);
      const clampedEndMinutes = Math.min(totalMinutesInRange, originalEndMinutes);

      // Calculate column indices (days for week/month, weeks for year)
      const startColIndex = Math.floor(clampedStartMinutes / minutesPerColumn);
      const endColIndex = Math.min(numDays - 1, Math.floor((clampedEndMinutes - 1) / minutesPerColumn));

      let left: number;
      let width: number;

      // Month/Year views: Jobs span full columns (no time-of-day positioning)
      if (viewMode === 'month' || viewMode === 'year') {
        // Simple full-column spanning
        left = startColIndex * colWidth;
        width = (endColIndex - startColIndex + 1) * colWidth;
      } else {
        // Week view: Work window-based pixel calculation
        // For each day, convert time-of-day to position within work window
        const startDate = new Date(shift.start_time);
        const endDate = new Date(shift.end_time);

        const startTimeOfDayMinutes = startDate.getHours() * 60 + startDate.getMinutes();
        const endTimeOfDayMinutes = endDate.getHours() * 60 + endDate.getMinutes();

        // Clamp to work window
        const clampedStartTod = Math.max(dayStartMinutes, Math.min(dayEndMinutes, startTimeOfDayMinutes));
        const clampedEndTod = Math.max(dayStartMinutes, Math.min(dayEndMinutes, endTimeOfDayMinutes));

        // Calculate position within day column
        const startXInDay = ((clampedStartTod - dayStartMinutes) / workWindowMinutes) * colWidth;
        const endXInDay = ((clampedEndTod - dayStartMinutes) / workWindowMinutes) * colWidth;

        if (startColIndex === endColIndex) {
          // Single day shift: position within that day's column
          left = startColIndex * colWidth + startXInDay;
          width = endXInDay - startXInDay;
        } else {
          // Multi-day shift: spans from start position to end position
          left = startColIndex * colWidth + startXInDay;
          const endAbsoluteX = endColIndex * colWidth + endXInDay;
          width = endAbsoluteX - left;
        }
      }

      // Ensure minimum visible width
      width = Math.max(width, 4);

      const task = shift.crm_task ? taskMap.get(shift.crm_task) : null;

      positions.push({
        shift,
        task: task || null,
        startDayIndex: startColIndex,
        endDayIndex: endColIndex,
        lane: 0,
        left,
        width,
        clampedLeft,
        clampedRight,
        originalStartMinutes,
        originalEndMinutes,
      });
    });

    positions.sort((a, b) => a.originalStartMinutes - b.originalStartMinutes);

    return { positions, lanes: 1, invalidJobShifts };
  }

  /**
   * @deprecated Use getShiftsForTeam instead. This is kept for backward compatibility.
   */
  static getShiftsForWorkArea(
    shifts: Shift[],
    workAreaId: number,
    weekStart: Date,
    taskMap: Map<number, CrmTask>,
    totalWidth: number,
    settings?: OperationsSettings
  ): { positions: GanttShiftPosition[]; lanes: number } {
    // Delegate to getShiftsForTeam with workAreaId as both team and parent
    // This maintains backward compatibility but won't work correctly for job shifts
    const result = this.getShiftsForTeam(
      shifts,
      workAreaId,
      workAreaId,
      weekStart,
      taskMap,
      totalWidth,
      settings,
      true // Include regular shifts for backward compat
    );
    return { positions: result.positions, lanes: result.lanes };
  }

  static formatGanttDateRange(shift: Shift): string {
    const start = new Date(shift.start_time);
    const end = new Date(shift.end_time);
    const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

    const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
    const endTime = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`;

    const sameDay = start.toDateString() === end.toDateString();
    if (sameDay) {
      // Single day: "08:00–17:00"
      return `${startTime}–${endTime}`;
    }

    // Multi-day: "Mo, 08:00 → Do, 17:00"
    const startDay = dayNames[start.getDay()];
    const endDay = dayNames[end.getDay()];
    return `${startDay}, ${startTime} → ${endDay}, ${endTime}`;
  }

  static async moveShiftByDays(
    shiftId: number,
    deltaDays: number,
    newWorkAreaId?: number
  ): Promise<Shift> {
    return this.moveShiftByMinutes(shiftId, deltaDays * 24 * 60, newWorkAreaId);
  }

  static async moveShiftByMinutes(
    shiftId: number,
    deltaMinutes: number,
    newWorkAreaId?: number
  ): Promise<Shift> {
    const response = await axiosInstance.get<Shift>(`/api/shifts/${shiftId}/`);
    const shift = response.data;

    const currentStart = new Date(shift.start_time);
    const currentEnd = new Date(shift.end_time);

    const newStart = new Date(currentStart.getTime() + deltaMinutes * 60 * 1000);
    const newEnd = new Date(currentEnd.getTime() + deltaMinutes * 60 * 1000);

    const payload: any = {
      start_time: formatLocalDateTime(newStart),
      end_time: formatLocalDateTime(newEnd),
      employees_needed: shift.employees_needed || 1,
    };

    if (newWorkAreaId !== undefined) {
      payload.work_area = newWorkAreaId;
    } else {
      payload.work_area = shift.work_area;
    }

    return ShiftService.updateShift(shiftId, payload);
  }

  static async resizeShift(
    shiftId: number,
    newEndDateTime: string
  ): Promise<Shift> {
    const response = await axiosInstance.get<Shift>(`/api/shifts/${shiftId}/`);
    const shift = response.data;

    return ShiftService.updateShift(shiftId, {
      start_time: shift.start_time,
      end_time: newEndDateTime,
      employees_needed: shift.employees_needed || 1,
      work_area: shift.work_area as number,
    });
  }

  /**
   * Moves a shift to an absolute target time (preserves duration).
   * This avoids rounding issues with delta-based moves.
   */
  static async moveShiftToTime(
    shiftId: number,
    newStartDateTime: string,
    newWorkAreaId?: number
  ): Promise<Shift> {
    const response = await axiosInstance.get<Shift>(`/api/shifts/${shiftId}/`);
    const shift = response.data;

    const currentStart = new Date(shift.start_time);
    const currentEnd = new Date(shift.end_time);
    const durationMs = currentEnd.getTime() - currentStart.getTime();

    const newStart = new Date(newStartDateTime);
    const newEnd = new Date(newStart.getTime() + durationMs);

    const payload: any = {
      start_time: formatLocalDateTime(newStart),
      end_time: formatLocalDateTime(newEnd),
      employees_needed: shift.employees_needed || 1,
    };

    if (newWorkAreaId !== undefined) {
      payload.work_area = newWorkAreaId;
    } else {
      payload.work_area = shift.work_area;
    }

    pdebug('moveShiftToTime:', {
      shiftId,
      oldStart: shift.start_time,
      newStart: payload.start_time,
      newEnd: payload.end_time,
      durationMs,
    });

    return ShiftService.updateShift(shiftId, payload);
  }

  static async checkEmployeeConflicts(
    shiftId: number,
    employeeId: number
  ): Promise<EmployeeConflict[]> {
    try {
      const response = await axiosInstance.get<EmployeeConflict[]>(
        `/api/admin/shifts/${shiftId}/employee-conflicts/`,
        { params: { employee_id: employeeId } }
      );
      return response.data;
    } catch (error) {
      pdebugError('Failed to check employee conflicts:', error);
      return [];
    }
  }

  static async checkWorkAreaConflicts(
    shiftId: number,
    workAreaId: number
  ): Promise<WorkAreaConflict[]> {
    try {
      const response = await axiosInstance.get<WorkAreaConflict[]>(
        `/api/admin/shifts/${shiftId}/work-area-conflicts/`,
        { params: { work_area_id: workAreaId } }
      );
      return response.data;
    } catch (error) {
      pdebugError('Failed to check work area conflicts:', error);
      return [];
    }
  }

  static async resolveWorkAreaConflicts(
    shiftId: number,
    workAreaId: number
  ): Promise<PushConflictsResult> {
    const response = await axiosInstance.post<PushConflictsResult>(
      `/api/admin/operations/resolve-work-area-conflicts/`,
      { shift_id: shiftId, work_area_id: workAreaId }
    );
    return response.data;
  }

  // ============================================================================
  // FE-OPS-R31-2: Job Comments API Methods
  // ============================================================================

  /**
   * Fetches comments for a job or checklist item.
   * GET /api/admin/operations/jobs/{taskId}/comments/
   * @param checklistItemId - Optional: filter comments by checklist item
   */
  static async fetchJobComments(taskId: number, checklistItemId?: number): Promise<JobComment[]> {
    let url = `${BASE}jobs/${taskId}/comments/`;
    if (checklistItemId) {
      url += `?checklist_item_id=${checklistItemId}`;
    }
    pdebug('fetchJobComments:', { taskId, checklistItemId });

    try {
      const response = await axiosInstance.get<any>(url);
      const comments = parseList<JobComment>(response.data, ['comments', 'results']);
      pdebug('fetchJobComments success:', comments.length, 'comments');
      return comments;
    } catch (err: any) {
      pdebugError('fetchJobComments failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Creates a new comment for a job or checklist item.
   * POST /api/admin/operations/jobs/{taskId}/comments/
   * @param checklistItemId - Optional: attach comment to a checklist item
   */
  static async createJobComment(taskId: number, text: string, checklistItemId?: number): Promise<JobComment> {
    const url = `${BASE}jobs/${taskId}/comments/`;
    pdebug('createJobComment:', { taskId, checklistItemId, text: text.substring(0, 50) });

    const payload: { text: string; checklist_item_id?: number } = { text };
    if (checklistItemId) {
      payload.checklist_item_id = checklistItemId;
    }

    const response = await axiosInstance.post<JobComment>(url, payload);
    pdebug('createJobComment success:', response.data);
    return response.data;
  }

  /**
   * Deletes a job comment.
   * DELETE /api/admin/operations/job-comments/{commentId}/
   */
  static async deleteJobComment(commentId: number): Promise<void> {
    const url = `${BASE}job-comments/${commentId}/`;
    pdebug('deleteJobComment:', { commentId });

    await axiosInstance.delete(url);
    pdebug('deleteJobComment success');
  }

  // ============================================================================
  // FE-OPS-R31-2: Job Attachments API Methods
  // ============================================================================

  /**
   * Fetches attachments for a job or checklist item.
   * GET /api/admin/operations/jobs/{taskId}/attachments/
   * @param checklistItemId - Optional: filter attachments by checklist item
   */
  static async fetchJobAttachments(taskId: number, checklistItemId?: number): Promise<JobAttachment[]> {
    let url = `${BASE}jobs/${taskId}/attachments/`;
    if (checklistItemId) {
      url += `?checklist_item_id=${checklistItemId}`;
    }
    pdebug('fetchJobAttachments:', { taskId, checklistItemId });

    try {
      const response = await axiosInstance.get<any>(url);
      const attachments = parseList<JobAttachment>(response.data, ['attachments', 'results']);
      pdebug('fetchJobAttachments success:', attachments.length, 'attachments');
      return attachments;
    } catch (err: any) {
      pdebugError('fetchJobAttachments failed:', err?.response?.status, err?.message);
      return [];
    }
  }

  /**
   * Uploads an attachment to a job or checklist item.
   * POST /api/admin/operations/jobs/{taskId}/attachments/
   * Uses multipart/form-data
   * @param checklistItemId - Optional: attach to a checklist item
   */
  static async uploadJobAttachment(
    taskId: number,
    file: File,
    commentId?: number,
    checklistItemId?: number
  ): Promise<JobAttachment> {
    const url = `${BASE}jobs/${taskId}/attachments/`;
    pdebug('uploadJobAttachment:', { taskId, fileName: file.name, commentId, checklistItemId });

    const formData = new FormData();
    formData.append('file', file);
    if (commentId) {
      formData.append('comment_id', String(commentId));
    }
    if (checklistItemId) {
      formData.append('checklist_item_id', String(checklistItemId));
    }

    const response = await axiosInstance.post<JobAttachment>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    pdebug('uploadJobAttachment success:', response.data);
    return response.data;
  }

  /**
   * Deletes a job attachment.
   * DELETE /api/admin/operations/job-attachments/{attachmentId}/
   */
  static async deleteJobAttachment(attachmentId: number): Promise<void> {
    const url = `${BASE}job-attachments/${attachmentId}/`;
    pdebug('deleteJobAttachment:', { attachmentId });

    await axiosInstance.delete(url);
    pdebug('deleteJobAttachment success');
  }
}

export interface EmployeeConflict {
  type: 'SHIFT' | 'ABSENCE' | 'AVAILABILITY';
  shift_id?: number;
  absence_id?: number;
  start_time: string;
  end_time: string;
  description: string;
}

export interface EmployeeAssignment {
  id: number;
  employee_id: number;
  employee_name: string;
  shift_id?: number;
}

export interface EmployeeJobConflict {
  shift_id: number;
  work_area_team_id: number;
  work_area_team_name: string;
  crm_task_id?: number;
  crm_task_title?: string;
  start_time: string;
  end_time: string;
  employee_name?: string;
}

// Asset Types
export interface Asset {
  id: number;
  name: string;
  code?: string;
  asset_type?: 'VEHICLE' | 'TOOL' | 'EQUIPMENT' | 'TRAILER' | 'IT_DEVICE' | 'OTHER';
  category?: string;
  short_description?: string;
  serial_number?: string;
  year?: number;
  license_plate?: string;
  status?: 'ACTIVE' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'RETIRED';
  is_active?: boolean;
}

export interface AssetAssignment {
  id: number;
  asset_id: number;
  asset_name: string;
  asset_type?: string;
  asset_status?: string;
  shift_id?: number;
}

export interface AssetConflict {
  shift_id: number;
  work_area_team_id: number;
  work_area_team_name: string;
  crm_task_id?: number;
  crm_task_title?: string;
  start_time: string;
  end_time: string;
}

export interface AvailableAsset extends Asset {
  availability_status?: 'available' | 'busy';
  busy_reason?: string;
  is_assigned?: boolean;
  conflict?: AssetConflict;
}

export interface WorkAreaConflict {
  shift_id: number;
  task_title?: string;
  start_time: string;
  end_time: string;
  overlap_minutes: number;
}

export interface PushConflictsResult {
  success: boolean;
  moved_shifts: MovedShift[];
  message: string;
}

export interface MovedShift {
  shift_id: number;
  task_title?: string;
  old_start: string;
  old_end: string;
  new_start: string;
  new_end: string;
}

// FE-OPS-R31-2: Job Comments and Attachments interfaces
export interface JobComment {
  id: number;
  job_id: number;
  checklist_item_id?: number | null;  // Subtask comments support
  author_id: number;
  author_name: string;
  text: string;
  created_at: string;
  updated_at?: string;
}

export interface JobAttachment {
  id: number;
  job_id: number;
  comment_id?: number;
  checklist_item_id?: number | null;  // Subtask attachments support
  file_name: string;
  file_size: number;
  content_type: string;
  file_url: string;
  thumbnail_url?: string;
  uploaded_by_id?: number;
  uploaded_by_name?: string;
  created_at: string;
}
